import { AUTH_TOKEN } from 'constants/keyNames';

type CheckErrorType = {
  code?: number;
  res?: Response;
};

const checkIfErrorOccurs = (res: Response): CheckErrorType => ({
  code: res.status,
  res,
});

const TIME_OUT = 30000;

type CustomHeadersInit = HeadersInit & {
  Authorization?: string;
};

interface CustomRequestInit extends RequestInit {
  headers: CustomHeadersInit;
}

async function customFetch(path: string, headerOptions: CustomRequestInit) {
  const normalFetch = fetch(path, headerOptions);
  const res: CheckErrorType = await timeoutPromise(
    TIME_OUT,
    normalFetch.then(checkIfErrorOccurs).catch(checkIfErrorOccurs),
  );

  if (!res.code) {
    const error = {
      code: 404,
      message: 'Fail to fetch',
    };
    throw error;
  }

  if (res.code < 300) {
    const response = await res.res.text();
    try {
      const jsonResponse = JSON.parse(response);
      return jsonResponse;
    } catch (error) {
      return response;
    }
  }
  try {
    const response = await res.res.json();
    const error = {
      code: res.code,
      ...response,
    };
    throw error;
  } catch (e) {
    if (res.code === 426) {
      const error = {
        code: res.code,
        message:
          'We have had some significant upgrades for the app. Please click below to upgrade your app!',
      };
      throw error;
    } else {
      const error = {
        code: res.code,
        message:
          e.error || e.message
            ? e.message || e.error
            : 'Something wrong. Please try again.',
      };
      throw error;
    }
  }
}

export const timeoutPromise = (ms: number, promise: Promise<CheckErrorType>) =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Request time out! Please try again.'));
    }, ms);
    promise.then(
      (res) => {
        clearTimeout(timeoutId);
        resolve(res);
      },
      (err) => {
        clearTimeout(timeoutId);
        reject(err);
      },
    );
  });

export default customFetch;

function requestWrapper(method: string) {
  const request = async (
    url: string,
    data = null,
    params = {},
    prefix = '',
  ) => {
    let convertUrl = `${process.env.REACT_APP_SERVER_URL}${prefix}${url}`;
    let convertParams = params;
    let convertData = data;
    if (method === 'GET') {
      // is it a GET?
      // GET doesn't have data
      convertParams = convertData;
      if (convertParams !== null) {
        convertUrl = `${convertUrl}${
          getQueryString(convertParams)
            ? `?${getQueryString(convertParams)}`
            : getQueryString(convertParams)
        }`;
      }
      convertData = null;
    } else if (convertData === Object(convertData)) {
      convertData = JSON.stringify(convertData);
    }

    const defaults: CustomRequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
      },
    };
    // check that req url is relative and request was sent to our domain
    const token = localStorage.getItem(AUTH_TOKEN);
    if (token) {
      defaults.headers.Authorization = `Bearer ${token}`;
    }

    if (method === 'POST' || method === 'PUT') {
      // defaults.headers.Accept = 'application/json';
      defaults.headers['Content-Type'] = 'application/json';
    }

    if (convertData) {
      defaults.body = convertData;
    }

    const paramsObj = {
      ...defaults,
      headers: { ...params, ...defaults.headers },
    };
    return customFetch(convertUrl, paramsObj);
  };
  return request;
}

export function getQueryString(params) {
  const esc = encodeURIComponent;
  return Object.keys(params)
    .filter((k) => params[k] || params[k] === 0)
    .map((k) => {
      const tmp = [];
      if (Array.isArray(params[k])) {
        params[k].forEach((k1) => {
          tmp.push(`${esc(k)}=${esc(k1)}`);
        });
        return tmp.join('&');
      } else {
        return `${esc(k)}=${esc(params[k])}`;
      }
    })
    .join('&');
}
export const get = requestWrapper('GET');
export const post = requestWrapper('POST');
export const put = requestWrapper('PUT');
export const patch = requestWrapper('PATCH');
export const del = requestWrapper('DELETE');
